(ns frontend.views.retrofit-quiz
  (:require
   [common.css              :as css]
   [common.furnace-patterns :as fp]
   [common.util             :as u]
   [opengb.i18n             :refer [Tr tr]]
   [reagent.core            :as r]
   [reagent.dom.client      :as rg-dom-client]
   [shadow.graft            :as graft]
   [tailwind-hiccup.core    :refer [tw]]))

(defn- ctx->field-data
  [ctx field-id]
  (get-in ctx [:form/fields field-id]))

(defn Section
  [title & contents]
  (into [:section (tw [:w-full :flex :flex-col :gap-y-5 :pt-8])
         (when title [:h2 (tw css/section-title) title])]
        contents))

(defn- markup-as-required
  "Adds the star for required questions to the string `s`"
  [s]
  [:span (tw [:text-red-1])
   (str "* " s)])

(defn ErrorWrapper
  [error? & contents]
  [:div (tw [:flex :gap-x-2])
   (when error?
     [:div (tw [:bg-red-1 :w-2 :shrink-0])])
   (into [:div] contents)])

(defn Dropdown [*value field-data]
  (let [{:keys [input-name
                description
                title
                options
                default-value
                error?]} field-data
        show-as-required? (and error? (:required field-data))]
    [ErrorWrapper error?
     [:div
      [:label (tw [:block :font-medium])
       (cond-> title
         show-as-required? (markup-as-required))]
      (when description
        [:p (tw [:italic :text-sm]) description])
      [:select (tw (if error? css/select-error css/select)
                   [:max-w-xs]
                   (merge (select-keys field-data [:required :name :value :options])
                          {:name input-name :value (or @*value "")
                           :on-change #(reset! *value (-> % .-target .-value))}))
       (when default-value
         [:option {:value ""} default-value])
       (for [option options]
         ^{:key option}
         [:option {:value option} option])]]]))

(defn TextInput
  [field-data]
  (let [{:keys [input-name title default-value description placeholder error?]} field-data
        show-as-required? (and error? (:required field-data))]
    [ErrorWrapper error?
     [:div
      [:label (tw [:block :font-medium])
       (cond-> title
         show-as-required? (markup-as-required))]
      (when description
        [:p (tw [:italic :text-sm]) description])
      [:input (tw (if error? css/select-error css/select)
                  [:max-w-xs]
                  (merge (select-keys field-data [:required :name :value :placeholder])
                         {:name input-name
                          :value default-value
                          :placeholder placeholder
                          :type "text"}))]]]))

(defn SerialNumberInput
  [*value field-data]
  (let [{:keys [input-name title description placeholder error?]} field-data
        show-as-required? (and error? (:required field-data))]
    [ErrorWrapper error?
     [:div
      [:label (tw [:block :font-medium])
       (cond-> title
         show-as-required? (markup-as-required))]
      (when description
        [:p (tw [:italic :text-sm]) description])
      [:input (tw (if error? css/select-error css/select)
                  [:max-w-xs]
                  (merge (select-keys field-data [:required :name :value :placeholder])
                         {:name input-name
                          :value @*value
                          :on-change #(reset! *value (-> % .-target .-value .toUpperCase))
                          :placeholder placeholder
                          :type "text"}))]]]))

(defn NumberInput [field-data]
  (let [{:keys [input-name
                title
                default-value
                description
                error?]} field-data
        show-as-required? (and error? (:required field-data))]
    [ErrorWrapper error?
     [:div
      [:label (tw [:block :font-medium])
       (cond-> title
         show-as-required? (markup-as-required))]
      (when description
        [:p (tw [:italic :text-sm]) description])
      [:input (tw (if error?
                    [:bg-red-3 :border-neutral-0]
                    [:bg-neutral-9 :border-neutral-4])
                  [:border :px-3 :py-1 :w-full :max-w-xs]
                  (merge (select-keys field-data [:required :placeholder :min :max :step])
                         {:name input-name
                          :default-value (str default-value)
                          :type "number"}))]]]))

(defn FurnaceDetails
  "Renders the furnace details section of the retrofit quiz.
   `furnace-fields` are the params for each field."
  [furnace-fields]
  (let [make (:furnace-make furnace-fields)
        *make-value (r/atom (:value make))
        serial (:furnace-serial furnace-fields)
        *serial-value (r/atom (:value serial))
        make->compiled-patterns (:make->compiled-patterns furnace-fields)]
    ;; following https://reagent-project.github.io/
    (fn []
      (let [guessed-yoms (fp/make+serial->possible-years-of-manufacture make->compiled-patterns @*make-value @*serial-value)
            ;; we choose the most recent year if there are multiple options
            ;; since it is unlikely to be old
            guessed-yom (some-> guessed-yoms
                                (not-empty)
                                (sort)
                                (last))
            yom-field (cond-> (:year furnace-fields)
                        guessed-yom (assoc :default-value guessed-yom))]
        [Section nil
         [:p "If you don't know how old your furnace is, or if you want a more detailed retrofit roadmap, please provide us with more information about your furnace."]
         [:div (tw [:flex :flex-row :gap-x-3])
          [Dropdown *make-value make]
          (when (= @*make-value "Other")
            [TextInput (get furnace-fields :furnace-make-other)])]
         [SerialNumberInput *serial-value serial]
         (if (not-empty guessed-yoms)
           [:div (tw [:flex :flex-col :gap-y-3])
            [:p "From the information provided, we estimate that your furnace was manufactured in (one of) the following year(s):"]
            [:ul (tw [:list :list-disc :list-inside :font-bold])
             (for [yom guessed-yoms]
               ^{:key yom}
               [:li yom])]
            [:p "Please confirm that your furnace was most likely manufactured in:"]
            [NumberInput yom-field]]
           [:div (tw [:flex :flex-col :gap-y-3])
            [:p "We are unable to guess the year of manufacture from the information provided."]
            [:p "If you know the year of manufacture, please enter it below."]
            [NumberInput (-> (:year furnace-fields)
                             (assoc :value ""))]])]))))

(defmethod graft/scion (u/->scion-id :frontend.views.retrofit-quiz/FurnaceDetails)
  [furnace-fields container]
  (let [root (rg-dom-client/create-root container)]
    (rg-dom-client/render root [FurnaceDetails furnace-fields])))

(defn- year->furnace-age-bucket
  "Currently the model expects there to always be a selected furnace 'bucket', even when
   we also have an exact year (based on the manufacturer and serial number).
   When an exact year is provided, the model uses that and ignores the age bucket, so
   these values are never used. However the model input validation does require that the
   age bucket is populated, so we just set it to a value that makes sense based on the actual year.
   This way the v2 quiz is compatible with the v1 quiz handler."
  [year]
  (let [age (- (fp/get-current-year) year)]
    (cond
      (< age 5)   "Less than 5 years"
      (<= age 10) "Between 5 and 10 years"
      (<= age 15) "Between 10-15 years"
      :else       "More than 15 years")))

(defn- furnace-age-radio
  [value selected-value error-id on-change]
  [:label (tw [:flex :items-center :gap-x-3 :py-1 :cursor-pointer])
   [:input (tw [:h-5 :w-5 :accent-blue-1]
               (cond-> {:type "radio" :name "furnace_age_estimate" :value value
                        :checked (= selected-value value)
                        :on-change #(on-change value)}
                 error-id (assoc :aria-invalid "true" :aria-describedby error-id)))]
   [Tr [:backend.views.retrofit-quiz/furnace-age-option value]]])

(def ^:private furnace-age-card
  ^:tw [:flex-1 :flex :flex-col :p-5 :rounded-lg :border :border-neutral-6 :bg-neutral-11])

(defn- FurnaceAgeEstimateCard
  [selected age-options error-id on-select]
  [:div (tw furnace-age-card)
   [:p (tw [:font-bold :text-lg :text-neutral-2]) [Tr :backend.views.retrofit-quiz/furnace-age-estimate-title]]
   [:p (tw [:text-sm :text-neutral-3 :pb-3]) [Tr :backend.views.retrofit-quiz/furnace-age-best-estimate]]
   [:div (tw [:flex :flex-col :gap-y-1])
    (for [opt age-options]
      ^{:key opt} [furnace-age-radio opt selected error-id on-select])]])

(defn- FurnaceAgeSerialCard
  "Card where you can select a furnace manufacturer and serial number.
  If decoded year is non-nil, we show it to the user; otherwise show the user that furnace age
  cannot be determined."
  [{:keys [make serial make-options decoded-year attempted? on-make on-serial on-serial-commit]}]
  [:div (tw furnace-age-card)
   [:p (tw [:font-bold :text-lg :text-neutral-2]) [Tr :backend.views.retrofit-quiz/furnace-age-serial-title]]
   [:p (tw [:text-sm :text-neutral-3 :pb-3]) [Tr :backend.views.retrofit-quiz/furnace-age-serial-subtitle]]
   [:div (tw [:flex :flex-col :gap-y-3])
    [:div
     [:label (tw [:block :font-medium :pb-1]) [Tr :backend.views.retrofit-quiz/furnace-age-manufacturer]]
     [:select (tw css/select-light
                  {:name       "furnace_make"
                   :value      make
                   :aria-label (tr :backend.views.retrofit-quiz/furnace-age-manufacturer)
                   :on-change  on-make})
      [:option {:value ""} (tr :backend.views.retrofit-quiz/furnace-age-select)]
      (for [m make-options]
        ^{:key m} [:option {:value m} m])]]
    [:div
     [:label (tw [:block :font-medium :pb-1]) [Tr :backend.views.retrofit-quiz/furnace-age-serial]]
     [:input (tw css/select-light
                 {:type        "text"
                  :name        "furnace_serial"
                  :aria-label  (tr :backend.views.retrofit-quiz/furnace-age-serial)
                  :placeholder (tr :backend.views.retrofit-quiz/furnace-age-serial-placeholder)
                  :value       serial
                  :on-change   on-serial
                  :on-blur     on-serial-commit})]]]
   (cond
     decoded-year
     [:p (tw [:font-medium :text-green-0 :pt-3])
      [Tr :backend.views.retrofit-quiz/furnace-age-determined] " " decoded-year "."]
     attempted?
     [:p (tw [:font-medium :text-neutral-3 :pt-3])
      [Tr :backend.views.retrofit-quiz/furnace-age-could-not-determine]])])

(defn QuizV2FurnaceAge
  "Furnace-age input offering two peer paths — estimate the age, or look it up by
   manufacturer + serial number (which computes an exact year). Done browser-side;
   the backend survey handler doesn't know how the furnace age was determined.
   Year is required: if year cannot be determined from manufacturer + serial number,
   then user must make a selection for estimated age."
  [{:keys [age-value age-options make-value make-options serial-value make->compiled-patterns error-id]}]
  (let [*choice           (r/atom (or age-value ""))
        *make             (r/atom (or make-value ""))
        *serial           (r/atom (or serial-value ""))
        ;; Only check if the serial number is valid on blur - otherwise it would show "invalid" while they are typing.
        *serial-committed (r/atom (or serial-value ""))]
    (fn []
      (let [decoded-year (some-> (fp/make+serial->possible-years-of-manufacture make->compiled-patterns @*make @*serial-committed)
                                 (sort)
                                 (last)
                                 (parse-long))
            furnace-age  (if decoded-year
                           (year->furnace-age-bucket decoded-year)
                           @*choice)]
        [:div (tw [:flex :flex-col])
         ;; "furnace_age" is the form input that sends the age range to the model
         [:input {:type  "hidden"
                  :name  "furnace_age"
                  :value furnace-age}]
         (when decoded-year
           ;; "year" is the input that sends the exact year to the model.
           ;; "year" is only populated if a valid model+serial number has been provided, otherwise model uses the "furnace_age" range.
           [:input {:type  "hidden"
                    :name  "year"
                    :value decoded-year}])
         [:p (tw [:font-medium :text-lg]) [Tr :backend.views.retrofit-quiz/furnace-age-question]]
         [:p (tw [:text-sm :text-neutral-3 :pb-4]) [Tr :backend.views.retrofit-quiz/furnace-age-subtitle]]
         [:div (tw [:flex :flex-col "md:flex-row" :gap-4 :items-stretch "md:items-start"])
          [FurnaceAgeEstimateCard @*choice age-options error-id #(reset! *choice %)]
          [:div (tw [:flex :items-center :justify-center :shrink-0 :self-stretch])
           [:span (tw [:flex :items-center :justify-center :h-8 :w-8 :rounded-full
                       :border :border-neutral-6 :bg-neutral-12 :text-xs :font-medium :text-neutral-3])
            [Tr :backend.views.retrofit-quiz/furnace-age-or]]]
          [FurnaceAgeSerialCard
           {:make             @*make
            :serial           @*serial
            :make-options     make-options
            :decoded-year     decoded-year
            :attempted?       (and (seq @*make) (seq @*serial-committed))
            :on-make          #(reset! *make (-> % .-target .-value))
            :on-serial        #(reset! *serial (-> % .-target .-value .toUpperCase))
            :on-serial-commit #(reset! *serial-committed @*serial)}]]]))))

(defmethod graft/scion (u/->scion-id :frontend.views.retrofit-quiz/QuizV2FurnaceAge)
  [data container]
  (let [root (rg-dom-client/create-root container)]
    (rg-dom-client/render root [QuizV2FurnaceAge data])))

(defn- record-input-modified
  "Records a survey-input-modified event every time the homeowner modifies a
  survey input. This allows us to track when a user has started filling out a
  survey for a building but hasn't submitted it."
  [^js form event-uri]
  (.addEventListener form "change"
                     (fn [_event]
                       (js/fetch event-uri #js {:method "POST" :keepalive true}))))

(defmethod graft/scion (u/->scion-id :frontend.views.retrofit-quiz/record-input-modified)
  [{:keys [event-uri]} form]
  (record-input-modified form event-uri))
